import { useMemo, useState } from "react";
import { ArrowUpRight, Check, Crown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LiquidPageHeader } from "@/components/liquid/LiquidPageHeader";
import { MagneticCard } from "@/components/liquid/MagneticCard";
import { ZoomParallax } from "@/components/liquid/ZoomParallax";
import { VelocityMarquee } from "@/components/liquid/VelocityMarquee";
import { VideoBackdrop } from "@/components/liquid/VideoBackdrop";
import { pageVideo } from "@/lib/videos";
import { PROCESS_STEPS } from "@/lib/content";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function TreatmentsLiquid() {
  const { content } = useContent();
  const s = content?.settings || {};
  const showPrices = s.show_prices !== "off";
  const { book } = useBooking();
  const treatments = content?.treatments || [];
  const packages = content?.packages || [];
  const [tab, setTab] = useState("All Treatments");
  const showPackages = content?.settings?.section_visibility?.treatments_packages ?? true;

  const categories = useMemo(() => ["All Treatments", ...new Set(treatments.map((t) => t.category))], [treatments]);
  const filtered = tab === "All Treatments" ? treatments : treatments.filter((t) => t.category === tab);

  return (
    <div className="relative z-[1] px-4 pb-24 pt-36 sm:px-8 sm:pt-44 lg:px-16" data-testid="treatments-liquid">
      <VideoBackdrop src={pageVideo(content?.settings, "treatments")} testid="treatments-video-bg" />
      <div className="mx-auto max-w-6xl">
        <LiquidPageHeader
          index="01"
          eyebrow="The Treatment Menu"
          title="Aesthetics, as Art"
          subtitle="Every protocol is physician-designed, clinically proven and tailored to your skin's unique story."
          testid="treatments-title"
        />

        <Reveal delay={0.3} className="mt-10">
          <div className="flex flex-wrap gap-x-8 gap-y-3" role="tablist" aria-label="Treatment categories">
            {categories.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={tab === c}
                onClick={() => setTab(c)}
                data-testid={`treatment-tab-${slug(c)}`}
                className={`border-b-2 pb-2 text-xs font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                  tab === c ? "border-[#D4AF37] text-[#1A1A1A]" : "border-transparent text-[#1A1A1A]/40 hover:text-[#B38B2D]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="treatments-grid">
          {filtered.map((t, i) => (
            <Reveal key={t.id} delay={(i % 3) * 0.08}>
              <div data-aura="true">
                <MagneticCard>
                  <article className="glass group flex h-full flex-col overflow-hidden rounded-[4px] shadow-[0_20px_50px_rgba(26,26,26,0.08)]" data-testid={`treatment-card-${t.id}`}>
                    <ZoomParallax src={t.image} alt={t.name} className="h-52" />
                    <div className="flex flex-1 flex-col p-7">
                      <p className="lc-lift text-[10px] font-medium uppercase tracking-[0.25em] text-[#B38B2D]">{t.category}</p>
                      <h3 className="lc-lift mt-2 font-display text-3xl font-normal text-[#1A1A1A]">{t.name}</h3>
                      <p className="lc-lift mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{t.tagline}</p>
                      <div className="mt-4 flex flex-wrap gap-2 text-[10px] font-medium uppercase tracking-[0.12em] text-[#1A1A1A]/45">
                        <span className="border border-[#1A1A1A]/10 px-2.5 py-1">{t.duration}</span>
                        <span className="border border-[#1A1A1A]/10 px-2.5 py-1">{t.downtime} downtime</span>
                      </div>
                      <div className="lc-reveal mt-auto flex items-center justify-between pt-6">
                        {showPrices && <span className="font-display text-2xl text-[#1A1A1A]">AED {t.price}</span>}
                        <button
                          onClick={() => book(t.name)}
                          data-testid={`treatment-book-${t.id}`}
                          aria-label={`Book ${t.name}`}
                          className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A1A1A] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#1A1A1A]"
                        >
                          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </article>
                </MagneticCard>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* PROCESS */}
      <section className="mx-auto mt-28 max-w-6xl" data-testid="process-section">
        <Reveal><p className="eyebrow">The Experience</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-3 font-display text-4xl font-normal text-[#1A1A1A] sm:text-5xl">Four Steps to Radiance</h2>
        </Reveal>
        <div className="mt-12 grid gap-px overflow-hidden rounded-[4px] bg-[#1A1A1A]/10 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS_STEPS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.08} className="bg-[#FAF8F5]">
              <div className="h-full p-8" data-testid={`process-step-${p.n}`}>
                <p className="font-display text-5xl italic text-[#D4AF37]/50">{p.n}</p>
                <h3 className="mt-5 font-display text-2xl font-normal text-[#1A1A1A]">{p.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#5A504B]">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <VelocityMarquee items={["Memberships", "Priority Booking", "Dedicated Physician", "VIP Lounge"]} />

      {/* PACKAGES */}
      {showPackages && packages.length > 0 && (
        <section className="mx-auto mt-24 max-w-6xl" data-testid="packages-section">
          <Reveal>
            <div className="rounded-[4px] bg-[#1A1615] px-6 py-16 sm:px-12 lg:px-16">
              <div className="mx-auto max-w-2xl text-center">
                <p className="eyebrow flex items-center justify-center gap-2 text-[#D4AF37]">
                  <Crown className="h-4 w-4" aria-hidden="true" /> VIP Skin Memberships
                </p>
                <h2 className="mt-3 font-display text-4xl font-normal text-white sm:text-5xl">Radiance, on Repeat</h2>
              </div>
              <div className="mt-12 grid gap-6 lg:grid-cols-3">
                {packages.map((p, i) => (
                  <Reveal key={p.id} delay={i * 0.12}>
                    <article
                      data-testid={`package-card-${p.id}`}
                      className={`relative flex h-full flex-col rounded-[4px] border p-8 transition-[transform,box-shadow] duration-500 ease-liquid hover:-translate-y-1.5 ${
                        p.highlighted ? "border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/15 to-transparent" : "border-white/10 bg-white/5"
                      }`}
                    >
                      {p.highlighted && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] px-4 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1A1615]">
                          Most Loved
                        </span>
                      )}
                      <h3 className="font-display text-2xl text-white">{p.name}</h3>
                      <p className="mt-4">
                        {showPrices && <span className="font-display text-4xl text-[#F3E5AB]">{p.price}</span>}
                        <span className="ml-2 text-xs uppercase tracking-[0.15em] text-[#A39288]">{p.period}</span>
                      </p>
                      <ul className="mt-6 flex-1 space-y-3">
                        {(p.features || []).map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm font-light text-[#E8D5CE]">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" /> {f}
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => book()} data-testid={`package-cta-${p.id}`} className={`mt-8 w-full px-6 py-3.5 text-sm ${p.highlighted ? "btn-gold" : "btn-ghost-lux border-[#D4AF37]/40 text-[#F3E5AB]"}`}>
                        {p.cta || "Join Now"}
                      </button>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      )}
    </div>
  );
}
